import { Controller } from "stimulus";
import { csrfToken } from "utils/json_request";

export default class AutoSaveController extends Controller {
    static targets = ["input", "inputGroup", "status"]
    static values = { disabled: Boolean }

    initialize() {
        this.changes = new Set([]);
    }

    // Every status target, not just the first: a form can carry one badge per section, and each
    // reads its own data-saved-text / data-unsaved-text.
    updateStatus() {
        for (const target of this.statusTargets) {
            if (this.disabledValue) {
                target.innerText = "Auto-save disabled";
            } else if (this.changes.size === 0) {
                target.innerText = target.dataset.savedText;
            } else {
                target.innerText = target.dataset.unsavedText;
            }
        }
    }

    statusTargetConnected() {
        this.updateStatus();
    }

    // Opt-in, for an auto-save form with no submit button. Enter in a field would otherwise fire
    // the browser's implicit submission - an HTML-format request that redirects, which on the live
    // console would throw a creator off the page mid-broadcast. Saving still goes through #save.
    preventSubmit(event) {
        event.preventDefault();
    }

    inputTargetConnected(el) {
        if (this.disabledValue) return;

        this.registerInput(el);
    }

    inputGroupTargetConnected(el) {
        if (this.disabledValue) return;

        let inputs = el.querySelectorAll('input, select');

        for (let input of inputs) {
            this.registerInput(input, el);
        }

        // Track children changes incase more inputs added/removed.
        // NB: episode timestamps use this functionality
        let observer = new MutationObserver((mutationList) => {
            for (const mutation of mutationList) {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach(node => {
                        let newInputs = node.querySelectorAll('input, select');
                        for (let input of newInputs) {
                            this.registerInput(input, el);
                        }
                    });

                    // If there were any fields removed we should trigger a save.
                    if (mutation.removedNodes.length > 0) {
                        this.save(inputs[0], el);
                    }
                }
            }
        });
        observer.observe(el, { childList: true, subtree: true });
    }

    // el:         input field to monitor for changes
    // inputGroup: the root el of the group to search for values
    registerInput(el, inputGroup = null) {
        let dirty = false;
        let autoSaveTimeout = null;
        let saving = false;

        const handleSave = async () => {
            if (saving) return;

            if (dirty) {
                saving = true;
                try {
                    if (await this.save(el, inputGroup)) {
                        this.changes.delete(inputGroup?.id || el.id);
                        this.updateStatus();
                        dirty = false;
                    }
                } finally {
                    saving = false;
                }
            }
        };

        const handleChange = () => {
            if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
            if (!dirty) {
                this.changes.add(inputGroup?.id || el.id);
                this.updateStatus();
            }
            dirty = true;
            autoSaveTimeout = setTimeout(handleSave, 10000);
        };

        el.addEventListener("trix-blur", handleSave);
        el.addEventListener("change", handleSave);
        el.addEventListener("trix-change", handleChange);
        el.addEventListener("input", handleChange);
    }

    async save(el, inputGroup = null) {
        let formData = new FormData();
        formData.append("authenticity_token", csrfToken());

        let inputs = [el];
        if (inputGroup) {
            inputGroup.querySelectorAll("input[name], select[name]").forEach(el => inputs.push(el));
        }

        // What each checkbox held when it went into the request, for showErrors to revert against.
        const sentChecked = new Map();

        for (let input of inputs) {
            // A disabled control submits nothing on a real form submit, and this has to match:
            // a control the page has disabled client-side is showing a forced value, not a choice.
            // The episode wizard locks Comments open on while an AMA thread is selected, and
            // without this, saving any other switch in the same group persisted that forced value
            // over the creator's own setting.
            if (input.disabled) continue;

            if (input.tagName === "SELECT") {
                if (input.selectedOptions.length > 0) {
                    for (let i = 0; i < input.selectedOptions.length; i++) {
                        formData.append(input.name, input.selectedOptions[i].value);
                    }
                } else {
                    // Special case so we know to delete any already selected options if
                    // all existing selections were cleared.
                    formData.append(input.name, 0);
                } 
            } else if (input.type === "file") {
                for (let file of input.files) {
                    formData.append(input.name, file);
                }
            } else if (input.type === "checkbox") {
                sentChecked.set(input, input.checked);
                if (input.checked) {
                    formData.append(input.name, input.value);
                }
            } else {
                formData.append(input.name, input.value);
            }
        }

        const payload = {
            method: "PATCH",
            headers: {
                "Accept": "application/json",
            },
            body: formData,
        };

        const form = el.tagName === "TRIX-EDITOR" ? el.inputElement.form : el.form;
        const anchor = inputGroup || el;
        if (anchor.nextElementSibling?.hasAttribute("data-auto-save-error")) anchor.nextElementSibling.remove();

        let data = null;
        try {
            data = await fetch(form.action, payload).then(res => res.json());
        } catch {
            // Network or parse failure: leave the field dirty so it retries on the next change
        }
        const ok = !!data?.ok;

        if (ok) {
            const event = new CustomEvent("change-saved");
            el.dispatchEvent(event);
            return true;
        }

        return this.showErrors(anchor, inputs, data?.errors, sentChecked);
    }

    // Surfaces the server's validation messages under the field that was rejected, so a refused change never looks saved. 
    // A rejected checkbox snaps back to the value the server kept (the opposite of what was sent). 
    // Returns true when every rejected field was a checkbox that's been put back, i.e. nothing is left unsaved. 
    // The message is plain text: the HTML re-render of the same error (a full submit) is what adds links.
    showErrors(anchor, inputs, errors, sentChecked) {
        if (!errors) return false;

        const messages = [];
        let settled = true;

        for (const [attribute, attributeMessages] of Object.entries(errors)) {
            const input = inputs.find(input => input.name === `episode[${attribute}]` && input.type === "checkbox");
            const field = input || inputs.find(input => input.name === `episode[${attribute}]`);
            if (!field) continue;

            messages.push(...attributeMessages);
            if (input) {
                // A checkbox that wasn't in the request (disabled) was never changed server-side.
                if (sentChecked.has(input)) input.checked = !sentChecked.get(input);
            } else {
                settled = false;
            }
        }

        if (messages.length === 0) return false;

        const error = document.createElement("p");
        error.dataset.autoSaveError = "";
        error.className = "mt-2 text-sm text-red-600";
        error.textContent = messages.join(" ");
        anchor.insertAdjacentElement("afterend", error);

        return settled;
    }
}
